import { HttpEventType } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FilesService } from 'app/shared/services/files.service';

@Component({
    selector: 'mh-core-lib-file-uploader',
    templateUrl: './file-uploader.component.html',
    styleUrls: ['./file-uploader.component.scss'],
    standalone: false
})
export class CoreLibFileUploaderComponent implements AfterViewInit, OnChanges {
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  @Input() customerId: number;
  @Input() completedActions = false;
  @Input() extensionPath = '';
  @Input() style: 'input' | 'chips' = 'input';
  @Input() uploadedFiles: { file_key: string; file_name: string }[] = [];
  @Input() customClasses:string = '';
  @Input() mockUpload = false;
  @Output() filesUploaded: EventEmitter<{ file_key: string; file_name: string }[]> = new EventEmitter();
  @Output() uploadingFilesProgress: EventEmitter<boolean> = new EventEmitter();
  @Output() emitFileInput: EventEmitter<HTMLInputElement> = new EventEmitter();
  @Output() fileRemoved: EventEmitter<{ file_key: string; file_name: string }> = new EventEmitter();

  fileTypesAccepted = [
    'image/*',
    'video/*',
    'application/pdf',
    'application/msword',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ];
  uploadingFiles = false;
  maxFileSize = 10;
  maxFileSizeError = false;
  maxNumberOfFiles = 4;
  maxNumberOfFilesError = false;

  internalFiles: { file_key: string; file_name: string }[] = [];

  constructor(
    private fileService: FilesService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.completedActions && changes.completedActions.currentValue) {
      this.internalFiles = [];
    }
    
    if (changes.uploadedFiles) {
      this.internalFiles = [...this.uploadedFiles];
    }
  }

  ngAfterViewInit(): void {
    // Emit the file input element to use it in the parent component
    this.emitFileInput.emit(this.fileInput.nativeElement);
  }

  handleFileInputClick(fileInput: HTMLInputElement) {
    fileInput.value = '';
    fileInput.click();
  }

  onFileInput(event) {
    if (this.mockUpload) {
      this.onFileInputMock(event);
      return;
    }

    this.uploadingFiles = true;
    this.maxFileSizeError = false;
    this.maxNumberOfFilesError = false;
    this.uploadingFilesProgress.emit(true);

    const filesToUpload = [...event.target.files];
    const filesToSubmit = [];

    if (this.internalFiles.length + filesToUpload.length > this.maxNumberOfFiles) {
      this.handleError('maxNumberOfFilesError');
      return false;
    }

    filesToUpload.every((file: File) => {
      let fileData;
      const size = file.size;
      const mbSize = parseInt((size / (1024 * 1024)).toFixed(2));
      if (mbSize > this.maxFileSize) {
        this.handleError('maxFileSizeError');
        return false;
      }

      const sanitizedFileName = this.sanitizeFileName(file.name);
      const sanitizedFile = new File([file], sanitizedFileName, { type: file.type });

      const data: FormData = new FormData();
      data.append('attachment', sanitizedFile);
      this.fileService.uploadPrivateWithEvents(this.customerId, data, this.extensionPath).subscribe((res) => {
        // if (res.type === HttpEventType.UploadProgress) {} // It can be used to show progress
        if (res.type === HttpEventType.Response) {
          fileData = {
            file_key: res.body.name,
            file_name: sanitizedFileName,
          };
          filesToSubmit.push(fileData);
          this.uploadingFiles = filesToSubmit.length !== filesToUpload.length;
          this.uploadingFilesProgress.emit(this.uploadingFiles);
          if (!this.uploadingFiles) {
            this.filesUploaded.emit(filesToSubmit);
            this.internalFiles = [...this.internalFiles, ...filesToSubmit];
          }
        }
      });
      return true; // return true to continue the loop
    });
  }

  private onFileInputMock(event: Event): void {
    this.maxFileSizeError = false;
    this.maxNumberOfFilesError = false;

    const filesToUpload: File[] = Array.from((event.target as HTMLInputElement).files);

    if (this.internalFiles.length + filesToUpload.length > this.maxNumberOfFiles) {
      this.handleError('maxNumberOfFilesError');
      return;
    }

    this.uploadingFiles = true;
    this.uploadingFilesProgress.emit(true);

    const mockDelay = 500 + Math.random() * 1000;
    setTimeout(() => {
      const mockFiles = filesToUpload.map((file) => ({
        file_key: `mock-${Date.now()}-${file.name}`,
        file_name: file.name,
      }));
      this.internalFiles = [...this.internalFiles, ...mockFiles];
      this.filesUploaded.emit(mockFiles);
      this.uploadingFiles = false;
      this.uploadingFilesProgress.emit(false);
    }, mockDelay);
  }

  removeFile(file: { file_key: string; file_name: string }) {
    this.internalFiles = this.internalFiles.filter(f => f.file_key !== file.file_key);
    this.fileRemoved.emit(file);
  }

  trackByFile(index: number, file: { file_key: string; file_name: string }): string {
    return file.file_key;
  }

  handleError = (error: string) => {
    this[error] = true;
    this.uploadingFiles = false;
  };

  private sanitizeFileName(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    const name = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
    const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';

    let sanitizedName = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .replace(/\s+/g, '-')
      .replace(/[^\p{L}\p{N}_-]/gu, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    if (!sanitizedName) sanitizedName = 'document';
    const sanitizedExt = extension.toLowerCase().replace(/[^a-z0-9.]/g, '') || '.bin';
    return sanitizedName + sanitizedExt;
  }
}
